import React, { createContext, useCallback, useContext, useRef, useState } from "react";

/* =========================================================
   Toast context
   Wrap the app once with <ToastProvider> (App.js) then anywhere:

     import { useToast } from "./components/common/Toast";
     const toast = useToast();
     toast.success("Target updated successfully");
     toast.error("Failed to delete achievement");
     toast.info("New achievement submitted");
========================================================= */

const ToastContext = createContext(null);

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const push = useCallback(
    (message, type = "info", title) => {
      const id = ++idCounter;
      setToasts((prev) => [...prev, { id, message, type, title }]);
      timers.current[id] = setTimeout(() => remove(id), 4000);
    },
    [remove]
  );

  const api = {
    success: (message, title = "Success") => push(message, "success", title),
    error: (message, title = "Error") => push(message, "error", title),
    info: (message, title = "Notice") => push(message, "info", title),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`} onClick={() => remove(t.id)}>
            <div className="toast-title">{t.title}</div>
            <div className="toast-msg">{t.message}</div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fail soft: components used outside the provider still work,
    // just without visible toasts, instead of crashing the app.
    return { success: () => {}, error: () => {}, info: () => {} };
  }
  return ctx;
}

export default ToastProvider;
